import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { apiFetch } from "../api/client.ts";
import {
  createSite,
  issueApiKey,
  listApiKeys,
  listSites,
  revokeApiKey,
  rotateWebhookSecret,
  updateSite,
  type ApiKeyIssued,
  type Site,
} from "../api/sites.ts";

interface BookingWindowRow {
  start: string;
  end: string;
}

interface BookingSettings {
  enabled: boolean;
  windows: Array<{ days: string[]; windows: BookingWindowRow[] }>;
  max_jobs_per_window: number;
  lead_time_hours: number;
  days_ahead: number;
  closed_dates: string[];
}

const DAY_ORDER: Array<{ code: string; label: string }> = [
  { code: "mo", label: "Mon" },
  { code: "tu", label: "Tue" },
  { code: "we", label: "Wed" },
  { code: "th", label: "Thu" },
  { code: "fr", label: "Fri" },
  { code: "sa", label: "Sat" },
  { code: "su", label: "Sun" },
];

const DEFAULT_BOOKING: BookingSettings = {
  enabled: false,
  windows: [
    {
      days: ["mo", "tu", "we", "th", "fr", "sa"],
      windows: [
        { start: "08:00", end: "10:00" },
        { start: "10:00", end: "12:00" },
        { start: "13:00", end: "15:00" },
        { start: "15:00", end: "17:00" },
      ],
    },
  ],
  max_jobs_per_window: 2,
  lead_time_hours: 3,
  days_ahead: 14,
  closed_dates: [],
};

export default function SitesPage() {
  const qc = useQueryClient();
  const { data: sites } = useQuery({ queryKey: ["sites"], queryFn: listSites });
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", domain: "", city: "", timezone: "UTC", locale: "en" });
  const [activeSite, setActiveSite] = useState<Site | null>(null);
  const [bookingSite, setBookingSite] = useState<string | null>(null);
  const [justIssued, setJustIssued] = useState<{ key?: string; webhook_secret?: string } | null>(null);

  const create = useMutation({
    mutationFn: () => createSite(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sites"] });
      setCreating(false);
      setForm({ name: "", domain: "", city: "", timezone: "UTC", locale: "en" });
    },
  });

  const issue = useMutation({
    mutationFn: (id: string) => issueApiKey(id, "plugin"),
    onSuccess: (data: ApiKeyIssued) => setJustIssued({ key: data.key }),
  });
  const rotate = useMutation({
    mutationFn: (id: string) => rotateWebhookSecret(id),
    onSuccess: (data) => setJustIssued({ webhook_secret: data.webhook_secret }),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-slate-900">Sites</h1>
        <div className="flex flex-wrap items-center gap-2">
          <a
            href="/plugin/websquad-plugin-latest.zip"
            className="btn-secondary"
            title="Download the latest WordPress plugin"
            download
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            <span className="hidden sm:inline">WP plugin</span>
          </a>
          <button type="button" className="btn" onClick={() => setCreating((x) => !x)}>
            {creating ? "Cancel" : "Add site"}
          </button>
        </div>
      </div>

      {creating && (
        <form
          className="card grid gap-3 md:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          {create.error && (
            <div className="md:col-span-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {(create.error as Error).message || "Request failed"}
            </div>
          )}
          <label className="block text-sm">
            <span className="mb-1 block font-medium">Name</span>
            <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium">Domain</span>
            <input className="input" value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} required />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium">City</span>
            <input className="input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium">Timezone</span>
            <input className="input" value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium">Locale</span>
            <select className="input" value={form.locale} onChange={(e) => setForm({ ...form, locale: e.target.value })}>
              <option value="en">English (en)</option>
              <option value="he">Hebrew (he)</option>
            </select>
          </label>
          <div className="md:col-span-2">
            <button type="submit" className="btn">Create site</button>
          </div>
        </form>
      )}

      {justIssued && (
        <div className="card border-l-4 border-l-amber-500">
          <h3 className="font-semibold">Store this secret now — shown once</h3>
          {justIssued.key && (
            <p className="mt-2 text-sm">
              <span className="text-slate-500">API key:</span>{" "}
              <code className="break-all rounded bg-slate-100 px-2 py-1">{justIssued.key}</code>
            </p>
          )}
          {justIssued.webhook_secret && (
            <p className="mt-2 text-sm">
              <span className="text-slate-500">Webhook secret:</span>{" "}
              <code className="break-all rounded bg-slate-100 px-2 py-1">{justIssued.webhook_secret}</code>
            </p>
          )}
          <button type="button" className="btn-secondary mt-3" onClick={() => setJustIssued(null)}>
            I saved it
          </button>
        </div>
      )}

      <div className="space-y-3">
        {sites?.map((s) => (
          <div key={s.id} className="card">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="min-w-0">
                <div className="font-semibold">{s.name}</div>
                <div className="break-all text-sm text-slate-500">{s.domain} · {s.city ?? "—"} · {s.locale}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" className="btn-secondary" onClick={() => issue.mutate(s.id)}>
                  <span className="sm:hidden">+ Key</span>
                  <span className="hidden sm:inline">Issue API key</span>
                </button>
                <button type="button" className="btn-secondary" onClick={() => rotate.mutate(s.id)}>
                  <span className="sm:hidden">↻ Secret</span>
                  <span className="hidden sm:inline">Rotate webhook secret</span>
                </button>
                <button type="button" className="btn-secondary" onClick={() => setActiveSite(activeSite?.id === s.id ? null : s)}>
                  <span className="sm:hidden">{activeSite?.id === s.id ? "Hide" : "Keys"}</span>
                  <span className="hidden sm:inline">{activeSite?.id === s.id ? "Hide keys" : "Show keys"}</span>
                </button>
                <button type="button" className="btn-secondary" onClick={() => setBookingSite(bookingSite === s.id ? null : s.id)}>
                  <span className="sm:hidden">📅</span>
                  <span className="hidden sm:inline">{bookingSite === s.id ? "Hide booking" : "Booking"}</span>
                </button>
              </div>
            </div>
            {activeSite?.id === s.id && <KeysPanel siteId={s.id} />}
            {bookingSite === s.id && <BookingPanel site={s} />}
          </div>
        ))}
        {!sites?.length && <p className="text-sm text-slate-500">No sites yet.</p>}
      </div>
    </div>
  );
}

function KeysPanel({ siteId }: { siteId: string }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["apiKeys", siteId], queryFn: () => listApiKeys(siteId) });
  const rev = useMutation({
    mutationFn: (keyId: string) => revokeApiKey(siteId, keyId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["apiKeys", siteId] }),
  });
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="text-left text-slate-500">
          <tr>
            <th>Prefix</th>
            <th>Label</th>
            <th className="hidden md:table-cell">Created</th>
            <th className="hidden md:table-cell">Revoked</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {data?.map((k) => (
            <tr key={k.id} className="border-t border-slate-100">
              <td className="py-1 font-mono">{k.key_prefix}…</td>
              <td>{k.label ?? "—"}</td>
              <td className="hidden md:table-cell">{new Date(k.created_at).toLocaleDateString()}</td>
              <td className="hidden md:table-cell">{k.revoked_at ? new Date(k.revoked_at).toLocaleDateString() : "—"}</td>
              <td>
                {!k.revoked_at && (
                  <button type="button" className="text-red-600 hover:underline" onClick={() => rev.mutate(k.id)}>
                    Revoke
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function normalizeBooking(raw: unknown): BookingSettings {
  const b = (raw ?? {}) as Partial<BookingSettings>;
  const first = Array.isArray(b.windows) && b.windows.length > 0 ? b.windows[0] : DEFAULT_BOOKING.windows[0];
  return {
    enabled: Boolean(b.enabled),
    windows: [
      {
        days: Array.isArray(first?.days) && first.days.length ? first.days : DEFAULT_BOOKING.windows[0].days,
        windows:
          Array.isArray(first?.windows) && first.windows.length
            ? first.windows.map((w) => ({ start: String(w.start ?? "08:00"), end: String(w.end ?? "10:00") }))
            : DEFAULT_BOOKING.windows[0].windows,
      },
    ],
    max_jobs_per_window: Number(b.max_jobs_per_window ?? DEFAULT_BOOKING.max_jobs_per_window),
    lead_time_hours: Number(b.lead_time_hours ?? DEFAULT_BOOKING.lead_time_hours),
    days_ahead: Number(b.days_ahead ?? DEFAULT_BOOKING.days_ahead),
    closed_dates: Array.isArray(b.closed_dates) ? b.closed_dates.map(String) : [],
  };
}

function BookingPanel({ site }: { site: Site }) {
  const qc = useQueryClient();
  const [cfg, setCfg] = useState<BookingSettings>(() => normalizeBooking(site.settings?.booking));
  const [closedText, setClosedText] = useState(cfg.closed_dates.join(", "));

  const save = useMutation({
    mutationFn: () => {
      const closed = closedText
        .split(/[,\s]+/)
        .map((x) => x.trim())
        .filter((x) => /^\d{4}-\d{2}-\d{2}$/.test(x));
      const booking: BookingSettings = { ...cfg, closed_dates: closed };
      return updateSite(site.id, { settings: { ...(site.settings ?? {}), booking } } as Partial<Site>);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sites"] }),
  });

  const rule = cfg.windows[0];
  const setRule = (patch: Partial<BookingSettings["windows"][0]>) =>
    setCfg({ ...cfg, windows: [{ ...rule, ...patch }] });

  return (
    <form
      className="mt-3 space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold">Online booking (website widget)</h3>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={cfg.enabled}
            onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
          />
          Enabled
        </label>
      </div>

      <div className="text-sm">
        <span className="mb-1 block font-medium">Booking days</span>
        <div className="flex flex-wrap gap-2">
          {DAY_ORDER.map((d) => (
            <label key={d.code} className="flex items-center gap-1 rounded border border-slate-200 bg-white px-2 py-1">
              <input
                type="checkbox"
                checked={rule.days.includes(d.code)}
                onChange={(e) =>
                  setRule({
                    days: e.target.checked
                      ? [...rule.days, d.code]
                      : rule.days.filter((x) => x !== d.code),
                  })
                }
              />
              {d.label}
            </label>
          ))}
        </div>
      </div>

      <div className="text-sm">
        <span className="mb-1 block font-medium">Arrival windows (site-local time)</span>
        <div className="space-y-2">
          {rule.windows.map((w, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="time"
                className="input w-32"
                value={w.start}
                onChange={(e) =>
                  setRule({ windows: rule.windows.map((x, j) => (j === i ? { ...x, start: e.target.value } : x)) })
                }
              />
              <span>–</span>
              <input
                type="time"
                className="input w-32"
                value={w.end}
                onChange={(e) =>
                  setRule({ windows: rule.windows.map((x, j) => (j === i ? { ...x, end: e.target.value } : x)) })
                }
              />
              <button
                type="button"
                className="text-red-600 hover:underline"
                onClick={() => setRule({ windows: rule.windows.filter((_, j) => j !== i) })}
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setRule({ windows: [...rule.windows, { start: "17:00", end: "19:00" }] })}
          >
            + Add window
          </button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <label className="block text-sm">
          <span className="mb-1 block font-medium">Max jobs per window</span>
          <input
            type="number"
            min={1}
            max={20}
            className="input"
            value={cfg.max_jobs_per_window}
            onChange={(e) => setCfg({ ...cfg, max_jobs_per_window: Number(e.target.value) || 1 })}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">Lead time (hours)</span>
          <input
            type="number"
            min={0}
            max={72}
            className="input"
            value={cfg.lead_time_hours}
            onChange={(e) => setCfg({ ...cfg, lead_time_hours: Number(e.target.value) || 0 })}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">Days ahead</span>
          <input
            type="number"
            min={1}
            max={60}
            className="input"
            value={cfg.days_ahead}
            onChange={(e) => setCfg({ ...cfg, days_ahead: Number(e.target.value) || 1 })}
          />
        </label>
      </div>

      <label className="block text-sm">
        <span className="mb-1 block font-medium">Closed dates (YYYY-MM-DD, comma separated)</span>
        <input
          className="input"
          placeholder="2026-11-26, 2026-12-25"
          value={closedText}
          onChange={(e) => setClosedText(e.target.value)}
        />
      </label>

      {save.error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {(save.error as Error).message || "Save failed"}
        </div>
      )}
      <div className="flex items-center gap-3">
        <button type="submit" className="btn" disabled={save.isPending}>
          {save.isPending ? "Saving…" : "Save booking settings"}
        </button>
        {save.isSuccess && <span className="text-sm text-emerald-600">Saved.</span>}
      </div>
      <p className="text-xs text-slate-500">
        Availability = these windows minus jobs already on the calendar (capacity {cfg.max_jobs_per_window}/window).
        The widget on {site.domain} hides full or past-cutoff windows automatically.
      </p>
    </form>
  );
}

// placate TS unused-import rule
void apiFetch;
